import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUp, ArrowUpRight, Building2, ChevronDown, Factory, House, Menu, Route, X } from "lucide-react";
import { COMPANY, MARKETS, MARKET_ORDER, FOOTER_TAGLINE, sized } from "@/lib/content";
import { Btn, Img } from "@/components/public/ui";
import { Logo, Wordmark } from "@/components/public/Logo";
import { EASE } from "@/components/public/Reveal";
import { ChatWidget } from "@/components/public/ChatWidget";
import { VARIANTS, VariantContext } from "@/lib/variant";
import { cn } from "@/lib/utils";

const PRIMARY = [
  { to: "/prisar", label: "Prisar" },
  { to: "/prosjekt", label: "Prosjekt" },
  { to: "/om-oss", label: "Om oss" },
  { to: "/karriere", label: "Jobb hos oss" },
  { to: "/kontakt", label: "Kontakt" },
];

/* Hide the header while scrolling down, bring it back as soon as the user scrolls up. */
function useHideOnScroll(disabled) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - last;
        if (y < 160) setHidden(false);
        else if (delta > 8) setHidden(true);
        else if (delta < -8) setHidden(false);
        last = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return disabled ? false : hidden;
}

/* One top-level item with a sliding hover pill and an active dot. */
function NavItem({ hovered, onHover, children, className = "", ...rest }) {
  return (
    <div className="relative" onMouseEnter={onHover} onFocus={onHover}>
      {hovered && (
        <motion.span
          layoutId="public-nav-pill"
          className="absolute inset-0 rounded-full bg-secondary"
          transition={{ type: "spring", stiffness: 520, damping: 42, mass: 0.6 }}
          aria-hidden
        />
      )}
      <div className={cn("relative z-10", className)} {...rest}>{children}</div>
    </div>
  );
}

const linkCls = (isActive) =>
  cn(
    "relative inline-flex h-9 items-center rounded-full px-3.5 text-[15px] font-medium transition-colors duration-150",
    isActive ? "text-foreground" : "text-foreground/65 hover:text-foreground"
  );

const ActiveDot = () => <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-dot" aria-hidden />;

const MENU_ICONS = { industri: Factory, samferdsel: Route, "bygg-naering": Building2, "service-bustad": House };
const MENU_LINKS = [
  ["/prisar", "Priseksempel på vanlege oppdrag", "public-services-menu-prisar"],
  ["/prosjekt", "Prosjekt vi har gjort", "public-services-menu-prosjekt"],
  ["/om-oss", "Om Kvinnherad Elektro", "public-services-menu-om-oss"],
  ["/karriere", "Jobb hos oss", "public-services-menu-karriere"],
  ["/kontakt", "Kontakt", "public-services-menu-kontakt"],
];

/* ------------------------------------------------------------------
   "Tenester" panel. Spans the content container like the bar above it.
   Three columns: the four markets (a highlight slides between them),
   a live preview of the one under the pointer (photo + what it covers),
   and a quiet column with the rest of the site and how to reach us.
   ------------------------------------------------------------------ */
function ServicesMenu({ open, setOpen, focusFirst }) {
  const reduce = useReducedMotion();
  const [preview, setPreview] = useState(0);
  // Preview photos are only fetched once the panel has been opened, so they never compete with the page's own images.
  const [armed, setArmed] = useState(false);
  useEffect(() => { if (open) setArmed(true); }, [open]);
  const listRef = useRef(null);
  const current = MARKETS[MARKET_ORDER[preview]];

  // Opened from the keyboard: move focus to the first market so Tab order continues inside the panel.
  useEffect(() => {
    if (!open || !focusFirst?.current) return;
    focusFirst.current = false;
    const id = requestAnimationFrame(() => listRef.current?.querySelector("a[data-market]")?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, focusFirst]);

  // Arrow keys move between the four markets; Home/End jump.
  const onListKey = (e) => {
    const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const links = Array.from(listRef.current?.querySelectorAll("a[data-market]") || []);
    const idx = links.indexOf(document.activeElement);
    let next = idx;
    if (e.key === "ArrowDown") next = (idx + 1) % links.length;
    if (e.key === "ArrowUp") next = (idx - 1 + links.length) % links.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = links.length - 1;
    links[next]?.focus();
  };

  // Always mounted; animated between open and closed so an exiting node can never linger over the page.
  // While closed it is inert (no focus, no pointer) and hidden from the tree once the close animation has settled.
  const [visible, setVisible] = useState(false);
  useEffect(() => { if (open) setVisible(true); }, [open]);

  return (
        <motion.div
          initial={false}
          animate={open ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: -6, scale: 0.995 }}
          transition={reduce ? { duration: 0 } : open ? { duration: 0.28, ease: EASE } : { duration: 0.14, ease: "easeOut" }}
          onAnimationComplete={() => { if (!open) setVisible(false); }}
          style={{ transformOrigin: "50% 0%", visibility: open || visible ? "visible" : "hidden" }}
          id="tenester-meny"
          className={cn("shadow-float absolute inset-x-0 top-0 mt-2.5 overflow-hidden rounded-[24px] border border-border/80 bg-background", !open && "pointer-events-none")}
          data-testid="public-services-menu"
          data-state={open ? "open" : "closed"}
          aria-hidden={!open}
          inert={!open}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,5fr)_minmax(0,4fr)_minmax(0,3fr)]">
            {/* 1. Markets */}
            <div className="p-3 sm:p-4">
              <p className="flex items-center justify-between px-3 pb-2 pt-2 text-[12.5px] font-medium text-muted-foreground">
                <span>Dette arbeider vi med</span>
                <span className="tabular">04</span>
              </p>
              <ul ref={listRef} onKeyDown={onListKey} className="relative grid gap-px">
                {MARKET_ORDER.map((slug, i) => {
                  const m = MARKETS[slug];
                  const Icon = MENU_ICONS[slug];
                  const on = preview === i;
                  return (
                    <li key={slug} className="relative">
                      {on && (
                        <motion.span
                          layoutId="services-menu-highlight"
                          className="absolute inset-0 rounded-2xl bg-secondary"
                          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 44, mass: 0.7 }}
                          aria-hidden
                        />
                      )}
                      <Link
                        to={`/${slug}`}
                        data-market={slug}
                        onClick={() => setOpen(false)}
                        onMouseEnter={() => setPreview(i)}
                        onFocus={() => setPreview(i)}
                        aria-current={on ? "true" : undefined}
                        className="group relative z-10 flex items-start gap-4 rounded-2xl px-3 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        data-testid={`public-services-menu-${slug}`}
                      >
                        <span className={cn("mt-px inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-200", on ? "bg-brand text-on-brand" : "bg-secondary text-foreground/70 group-hover:text-foreground")}>
                          <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-3">
                            <span className="text-[15.5px] font-medium text-foreground">{m.title}</span>
                            <ArrowUpRight className={cn("link-diag h-4 w-4 shrink-0 transition-colors duration-200", on ? "text-foreground" : "text-foreground/25")} strokeWidth={1.5} aria-hidden />
                          </span>
                          <span className="mt-0.5 block text-[13.5px] leading-snug text-muted-foreground">{m.short}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* 2. Live preview of the market under the pointer */}
            <div className="hidden border-l border-border/70 p-4 lg:flex lg:flex-col" data-testid="public-services-menu-preview">
              <div className="relative aspect-[3/2] overflow-hidden rounded-2xl bg-muted">
                {MARKET_ORDER.map((slug, i) => (
                  <motion.div
                    key={slug}
                    className="absolute inset-0"
                    initial={false}
                    animate={{ opacity: preview === i ? 1 : 0, scale: preview === i ? 1 : 1.05 }}
                    transition={{ duration: 0.55, ease: EASE }}
                    aria-hidden={preview !== i}
                  >
                    {armed && <Img src={MARKETS[slug].photo || sized(MARKETS[slug].preview, 900)} alt="" sizes="30vw" className="img-premium h-full w-full object-cover" style={{ objectPosition: MARKETS[slug].focus || "50% 50%" }} loading="eager" />}
                  </motion.div>
                ))}
              </div>
                <motion.div
                  key={current.slug}
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, ease: EASE }}
                  className="flex flex-1 flex-col px-1 pt-4"
                >
                  <p className="font-display text-[1.15rem] font-medium leading-tight tracking-[-0.02em]">{current.tagline || current.title}</p>
                  <ul className="mt-3 grid gap-1.5 text-[13.5px] text-muted-foreground">
                    {(current.highlights || []).slice(0, 4).map((h) => (
                      <li key={h} className="flex items-center gap-2.5">
                        <span className="h-1 w-1 shrink-0 rounded-full bg-dot" aria-hidden />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/${current.slug}`} onClick={() => setOpen(false)} className="link-arrow mt-auto inline-flex items-center gap-1.5 pt-4 text-[14.5px] font-medium text-foreground hover:text-brand" data-testid="public-services-menu-preview-link">
                    {current.cta} <ArrowRight className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                  </Link>
                </motion.div>
            </div>

            {/* 3. The rest of the site, and how to reach us */}
            <div className="hidden border-l border-border/70 p-4 xl:flex xl:flex-col">
              <p className="px-1 pb-2 pt-2 text-[12.5px] font-medium text-muted-foreground">Meir</p>
              <ul className="grid gap-0.5">
                {MENU_LINKS.map(([to, label, tid]) => (
                  <li key={to}>
                    <Link to={to} onClick={() => setOpen(false)} className="link-arrow flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-[14.5px] font-medium text-foreground/80 transition-colors duration-150 hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={tid}>
                      {label}
                      <ArrowRight className="h-3.5 w-3.5 text-foreground/30" strokeWidth={1.75} aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-auto rounded-2xl bg-secondary/80 p-4" data-testid="public-services-menu-contact">
                <p className="text-[12.5px] font-medium text-muted-foreground">Snakk med oss</p>
                <a href={COMPANY.phoneHref} className="u-link tabular mt-1.5 block w-fit font-display text-[1.35rem] font-medium tracking-[-0.02em] text-foreground" data-testid="public-services-menu-phone-link">{COMPANY.phone}</a>
                <a href={`mailto:${COMPANY.email}`} className="u-link mt-1 block w-fit text-[14px] text-foreground/75">{COMPANY.email}</a>
                <p className="mt-3 text-[12.5px] leading-snug text-muted-foreground">{COMPANY.hq.short} · {COMPANY.branches.join(" og ")}</p>
              </div>
            </div>
          </div>
        </motion.div>
  );
}

function MobileMenu({ open, onClose }) {
  const reduce = useReducedMotion();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const item = "flex items-center justify-between py-3 font-display text-[2rem] font-medium leading-none tracking-[-0.03em]";
  const stagger = (i) => ({
    initial: reduce ? false : { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.35, ease: EASE, delay: 0.04 * i },
  });
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="menu"
          data-testid="public-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Meny"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          transition={{ duration: 0.22, ease: EASE }}
          className="fixed inset-0 z-[70] flex flex-col bg-background text-foreground"
        >
          <div className="container-public flex h-16 items-center justify-between">
            <Logo onClick={onClose} size="sm" testid="public-mobile-wordmark" />
            <button onClick={onClose} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border hover:bg-secondary" aria-label="Lukk meny" data-testid="public-mobile-menu-close-button">
              <X className="h-5 w-5" strokeWidth={1.75} />
            </button>
          </div>
          <nav className="container-public flex-1 overflow-y-auto pt-8" aria-label="Mobilmeny">
            <motion.p {...stagger(0)} className="text-[13px] font-medium text-muted-foreground">Tenester</motion.p>
            <div className="mt-2">
              {MARKET_ORDER.map((slug, i) => (
                <motion.div key={slug} {...stagger(i + 1)}>
                  <NavLink to={`/${slug}`} onClick={onClose} data-testid={`public-mobile-nav-link-${slug}`} className={({ isActive }) => cn(item, isActive ? "text-foreground" : "text-foreground/85")}>
                    {MARKETS[slug].title}
                    <ArrowUpRight className="h-6 w-6 text-foreground/30" strokeWidth={1.5} aria-hidden />
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 border-t border-border pt-6">
              {PRIMARY.map((n, i) => (
                <motion.div key={n.to} {...stagger(i + 5)}>
                  <NavLink to={n.to} onClick={onClose} data-testid={`public-mobile-nav-link-${n.to.replace("/", "")}`} className={({ isActive }) => cn(item, isActive ? "text-foreground" : "text-foreground/85")}>
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <motion.div {...stagger(9)} className="mt-8 flex flex-col gap-2 border-t border-border pt-6 text-[16px]">
              <a href={COMPANY.phoneHref} className="tabular font-medium text-foreground" data-testid="public-mobile-phone-link">{COMPANY.phone}</a>
              <a href={`mailto:${COMPANY.email}`} className="text-foreground/80">{COMPANY.email}</a>
            </motion.div>
          </nav>
          <div className="container-public border-t border-border py-4">
            <Btn to="/kontakt" size="lg" className="w-full" onClick={onClose} data-testid="public-mobile-bestill-button">Send førespurnad</Btn>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const PublicLayout = ({ variant = "default" }) => {
  const v = VARIANTS[variant] || VARIANTS.default;
  const location = useLocation();
  const reduce = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [services, setServices] = useState(false);
  const [hover, setHover] = useState(null);
  const [focusWithin, setFocusWithin] = useState(false);
  const closeTimer = useRef(null);
  const servicesBtn = useRef(null);
  const headerRef = useRef(null);
  const focusFirst = useRef(false);
  const isMarket = MARKET_ORDER.some((s) => location.pathname === `/${s}`);
  const hidden = useHideOnScroll(services || open || focusWithin || reduce);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setOpen(false);
    setServices(false);
  }, [location.pathname, location.hash]);

  const closeServices = (returnFocus = false) => {
    clearTimeout(closeTimer.current);
    setServices(false);
    if (returnFocus) servicesBtn.current?.focus();
  };

  // Escape closes the services menu and returns focus to its trigger; clicking outside the header closes it too.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setServices((v) => {
        if (v) servicesBtn.current?.focus();
        return false;
      });
    };
    const onDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setServices(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, []);

  const enter = () => { clearTimeout(closeTimer.current); setServices(true); };
  const leave = () => { closeTimer.current = setTimeout(() => setServices(false), 150); };
  // Mouse click never fights the hover-open: it only ensures the menu is open. Keyboard (Enter/Space, detail === 0) toggles.
  const onServicesClick = (e) => {
    clearTimeout(closeTimer.current);
    if (e.detail === 0) {
      if (services) closeServices(true);
      else { focusFirst.current = true; setServices(true); }
    } else {
      setServices(true);
    }
  };
  // Arrow down on the trigger opens the panel and lands on the first market.
  const onServicesKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusFirst.current = true;
      setServices(true);
    }
  };

  const island = scrolled || services;

  // The legacy wordmark is set in Open Sans; load it only when that variant is shown.
  useEffect(() => {
    if (v.id !== "gammel" || document.getElementById("font-open-sans")) return;
    const link = document.createElement("link");
    link.id = "font-open-sans";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;800&display=swap";
    document.head.appendChild(link);
  }, [v.id]);

  return (
    <VariantContext.Provider value={v}>
    <div className={cn("min-h-screen bg-background text-foreground", v.themeClass)} data-variant={v.id}>
      <a href="#innhald" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-foreground focus:px-4 focus:py-2 focus:text-background">Hopp til innhaldet</a>

      <header
        ref={headerRef}
        className={cn("sticky top-0 z-50 transition-transform duration-300 ease-premium", hidden && "-translate-y-[130%]")}
        data-testid="public-nav"
        data-hidden={hidden ? "true" : "false"}
        onFocusCapture={() => setFocusWithin(true)}
        onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { setFocusWithin(false); setServices(false); } }}
      >
        {/* The bar runs edge to edge; its content sits on the same grid as the page below (container-public). Turns translucent once the page scrolls. */}
        <div className={cn("relative transition-[background-color,border-color,box-shadow] duration-300 ease-premium", island ? "border-b border-border/80 bg-background/85 shadow-[0_1px_0_hsl(var(--border)/0.4)] backdrop-blur-xl" : "border-b border-transparent bg-transparent")}>
          <div className="container-public flex h-16 items-center justify-between gap-6" data-testid="public-nav-inner">
            <Logo />

            <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Hovudmeny" onMouseLeave={() => setHover(null)}>
              <NavItem hovered={hover === "tenester"} onHover={() => setHover("tenester")} onMouseEnter={enter} onMouseLeave={leave}>
                <button
                  ref={servicesBtn}
                  type="button"
                  onClick={onServicesClick}
                  onKeyDown={onServicesKey}
                  aria-expanded={services}
                  aria-haspopup="true"
                  aria-controls="tenester-meny"
                  data-testid="public-nav-services-button"
                  className={cn(linkCls(isMarket || services), "gap-1")}
                >
                  Tenester <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", services && "rotate-180")} strokeWidth={1.75} />
                  {isMarket && <ActiveDot />}
                </button>
              </NavItem>
              {PRIMARY.map((n) => (
                <NavItem key={n.to} hovered={hover === n.to} onHover={() => setHover(n.to)}>
                  <NavLink to={n.to} data-testid={`public-nav-link-${n.to.replace("/", "")}`} className={({ isActive }) => linkCls(isActive)}>
                    {({ isActive }) => (<>{n.label}{isActive && <ActiveDot />}</>)}
                  </NavLink>
                </NavItem>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <a href={COMPANY.phoneHref} className="tabular hidden h-9 items-center rounded-full px-3 text-[14.5px] font-medium text-foreground/70 transition-colors hover:text-foreground xl:inline-flex" data-testid="public-nav-phone-link">{COMPANY.phone}</a>
              <Btn to="/kontakt" size="sm" arrow className="hidden h-10 px-4 text-[14.5px] sm:inline-flex" data-testid="public-nav-bestill-elektrikar-button">Send førespurnad</Btn>
              <button
                onClick={() => setOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-secondary lg:hidden"
                data-testid="public-nav-mobile-menu-button"
                aria-label="Opne meny"
                aria-expanded={open}
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>
          </div>
          {/* Outside the flex row so it never takes a slot in the space distribution; the panel spans the same container as the bar. */}
          <div onMouseEnter={enter} onMouseLeave={leave} className="container-public absolute inset-x-0 top-full hidden lg:block">
            <div className="relative">
              <ServicesMenu open={services} setOpen={setServices} focusFirst={focusFirst} />
            </div>
          </div>
        </div>
      </header>

      {/* Soft dim over the page while the services panel is open. Pointer-through so hover-out still closes it. */}
      <AnimatePresence>
        {services && (
          <motion.div
            key="services-dim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.18 } }}
            transition={{ duration: 0.3, ease: EASE }}
            className="pointer-events-none fixed inset-0 z-40 hidden bg-foreground/[0.08] backdrop-blur-[2px] lg:block"
            aria-hidden
            data-testid="public-services-dim"
          />
        )}
      </AnimatePresence>

      <MobileMenu open={open} onClose={() => setOpen(false)} />

      {/* Page fade on route change. No exit animation: AnimatePresence(mode="wait") left the new page stuck at opacity 0. */}
      <motion.main
        id="innhald"
        key={location.pathname}
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25, ease: EASE }}
      >
        <Outlet />
      </motion.main>

      <footer className="mt-24 border-t border-border sm:mt-32" data-testid="public-footer">
        <div className="container-public">
          <div className="grid gap-14 py-16 sm:py-20 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-5">
              <Wordmark size="md" />
              <p className="mt-6 max-w-sm text-[17px] leading-relaxed text-muted-foreground">{FOOTER_TAGLINE}</p>
              <div className="mt-10">
                <a href={COMPANY.phoneHref} className="u-link tabular font-display text-[1.75rem] font-medium tracking-[-0.025em] sm:text-[2rem]" data-testid="public-footer-phone-link">{COMPANY.phone}</a>
                <a href={`mailto:${COMPANY.email}`} className="u-link mt-3 block w-fit text-[17px]">{COMPANY.email}</a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-3 lg:col-span-7 lg:pt-1">
              <div>
                <p className="text-[13px] font-medium text-muted-foreground">Tenester</p>
                <ul className="mt-5 space-y-3 text-[15.5px]">
                  {MARKET_ORDER.map((slug) => (<li key={slug}><Link to={`/${slug}`} className="u-link">{MARKETS[slug].title}</Link></li>))}
                  <li><Link to="/prisar" className="u-link">Prisar</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-[13px] font-medium text-muted-foreground">Om selskapet</p>
                <ul className="mt-5 space-y-3 text-[15.5px]">
                  <li><Link to="/prosjekt" className="u-link">Prosjekt</Link></li>
                  <li><Link to="/om-oss" className="u-link">Om oss</Link></li>
                  <li><Link to="/karriere" className="u-link">Jobb hos oss</Link></li>
                  <li><Link to="/kontakt" className="u-link">Kontakt</Link></li>
                  <li><Link to="/drift/login" className="u-link inline-flex items-center gap-1" data-testid="public-footer-drift-link">Driftsportal <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden /></Link></li>
                </ul>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className="text-[13px] font-medium text-muted-foreground">Finn oss</p>
                <ul className="mt-5 space-y-5 text-[15.5px]">
                  <li>
                    <p className="font-medium">Hovudkontor</p>
                    <p className="mt-1 text-muted-foreground">{COMPANY.hq.short}</p>
                  </li>
                  <li>
                    <p className="font-medium">Postadresse</p>
                    <p className="mt-1 text-muted-foreground">{COMPANY.hq.post.map((l) => <span key={l} className="block">{l}</span>)}</p>
                  </li>
                  <li>
                    <p className="font-medium">Avdelingar</p>
                    <p className="mt-1 text-muted-foreground">{COMPANY.branchesText}</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 border-t border-border py-6 text-[13.5px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
              <span>© {new Date().getFullYear()} {COMPANY.legal}</span>
              <span className="hidden h-1 w-1 rounded-full bg-dot sm:inline-block" aria-hidden />
              <span>{COMPANY.group}</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://kvinnheradelektro.no" target="_blank" rel="noreferrer" className="u-link">kvinnheradelektro.no</a>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: reduce ? "instant" : "smooth" })}
                className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-3.5 text-foreground transition-colors hover:border-foreground"
                data-testid="public-footer-top-button"
              >
                Til toppen <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </footer>

      <ChatWidget />
    </div>
    </VariantContext.Provider>
  );
};
